/**
 * Data Transformer Utility
 *
 * Transforms the nested UI form data structure into the flat API request format
 */

import type { WorkflowFormData } from '../config/workflow';
import { PrimaryStep, SubStep } from '../config/steps';

/**
 * Flattens the nested workflow form data into a single-level object
 * suitable for the API's applicationData field
 */
export function transformFormDataToApiFormat(formData: WorkflowFormData): Record<string, unknown> {
  const apiData: Record<string, unknown> = {};

  // Iterate through all steps and substeps
  Object.entries(formData).forEach(([stepId, stepData]) => {
    Object.entries(stepData).forEach(([substepId, substepData]) => {
      // Merge all substep data into a flat structure
      // Using a prefix to avoid key collisions between steps
      Object.entries(substepData as Record<string, unknown>).forEach(([key, value]) => {
        // Create a namespaced key to prevent collisions
        const namespacedKey = `${stepId}__${substepId}__${key}`;
        apiData[namespacedKey] = value;
      });
    });
  });

  return apiData;
}

/**
 * Transforms API response data back into the nested UI structure
 */
export function transformApiFormatToFormData(apiData: Record<string, unknown>): WorkflowFormData {
  const formData: WorkflowFormData = {};

  // Parse namespaced keys back into nested structure
  Object.entries(apiData).forEach(([key, value]) => {
    const parts = key.split('__');

    if (parts.length === 3) {
      const [stepId, substepId, fieldKey] = parts;

      if (!formData[stepId]) {
        formData[stepId] = {};
      }

      if (!formData[stepId][substepId]) {
        formData[stepId][substepId] = {};
      }

      formData[stepId][substepId][fieldKey] = value;
    }
  });

  return formData;
}

/**
 * Alternative: Transform to a more structured format that preserves the step hierarchy
 * This keeps the data more readable in the database
 */
export function transformFormDataToStructuredApiFormat(formData: WorkflowFormData): Record<string, unknown> {
  return formData;
}

/**
 * Extract specific fields needed for application creation
 * This is used when creating the initial draft application
 */
export function extractApplicationMetadata(formData: WorkflowFormData): {
  applicationType: 'NEW_ACCESS' | 'RENEWAL' | 'MODIFICATION';
  contractId: string | null;
  laborCategoryId: string | null;
} {
  // Default to NEW_ACCESS - you may want to get this from user selection
  const applicationType = 'NEW_ACCESS';

  // Try to extract contractId and laborCategoryId from contract details step
  const contractDetails = formData[PrimaryStep.CONTRACT_DETAILS]?.[SubStep.CONTRACT_INFO];
  const contractId = (contractDetails?.contractId as string) || null;
  const laborCategoryId = (contractDetails?.laborCategoryId as string) || null;

  return {
    applicationType,
    contractId,
    laborCategoryId,
  };
}

/**
 * Check if we have enough data to create an application
 */
export function hasMinimumRequiredData(formData: WorkflowFormData): boolean {
  const contractDetails = formData[PrimaryStep.CONTRACT_DETAILS]?.[SubStep.CONTRACT_INFO];

  return !!(contractDetails?.contractId && contractDetails?.laborCategoryId);
}
